import * as React from 'react';
import { useGetCalendarEvents } from 'owa-timestream-asset-sdk-calendarevents';
import type { GoalPayload } from '../schema';
import { OwaDate, getDateString } from 'owa-datetime';
import {
    AssetCommandDescriptors,
    useAssetCommands,
    useAssetId,
    usePatchPartialPayload,
    useProjectHref,
} from 'owa-timestream-asset-sdk';
import { getDueDateMenuPicker, DueDateMenuPickerWrapper } from 'owa-timestream-asset-sdk-duedates';
import createGoalEvent from './createGoalEvent';
import deleteGoalEvent from './deleteGoalEvent';
import getDueDate from './getDueDate';
import updateGoalEvent from './updateGoalEvent';
import { logSpacesDatapoint } from 'owa-timestream-logging';

export default function useGoalEventSync({
    title = '',
    date = '',
    tz = '',
    isCompleted = false,
    calendarEventId = '',
}: GoalPayload) {
    const assetId = useAssetId();
    const [projectName, projectUrl, projectId] = useProjectHref();
    const { getCalendarEvent } = useGetCalendarEvents();

    // Fetch the event from the mobx store.
    // It is null if event was not created yet, was deleted, or if the store is still fetching it.
    // Tag the asset with 'DELETED' if we are told an event is not found, so we don't try to recreate it.
    const shouldLoadEvent = calendarEventId && calendarEventId != 'DELETED';
    const event = shouldLoadEvent
        ? getCalendarEvent(calendarEventId, function onGoalEventNotFound() {
              logSpacesDatapoint('OUTLOOKSPACES_GOAL_EVENT_NOT_FOUND');
              patchPartialPayload({ calendarEventId: 'DELETED' });
          })
        : null;

    // Calculate the effective values we're going to show.
    // At first we go with the values from the payload but once the event loads it will override them.
    const effectiveTitle = (event ? event.Subject : title) || '';
    const effectiveDueDate = event ? event.Start : getDueDate(date, tz);
    const effectiveDate = effectiveDueDate ? getDateString(effectiveDueDate) : '';
    const effectiveTz = effectiveDueDate ? effectiveDueDate.tz : '';

    // Patching date and title resets the 'DELETED' tag, so the event is re-created if the goal is edited.
    const calendarEventIdWhenPatching = calendarEventId == 'DELETED' ? '' : calendarEventId;
    const patchPartialPayload = usePatchPartialPayload<GoalPayload>();

    useAssetCommands(
        [
            {
                ...(!!effectiveDueDate
                    ? AssetCommandDescriptors.editDueDate
                    : AssetCommandDescriptors.setDueDate),
                getMenuProps: () => ({
                    focusZoneAs: DueDateMenuPickerWrapper,
                }),
                getMenuItems: () =>
                    getDueDateMenuPicker(
                        projectId,
                        assetId,
                        effectiveDueDate ?? undefined,
                        isCompleted,
                        false,
                        patchDate,
                        removeDate,
                        patchIsCompleted
                    ),
            },
        ],
        [assetId, effectiveDueDate, isCompleted]
    );

    const patchDate = React.useCallback(
        (newDate: OwaDate | null) => {
            const newDateStr = newDate ? getDateString(newDate) : '';
            const newDateTz = newDate ? newDate.tz : '';

            patchPartialPayload({
                date: newDateStr,
                tz: newDateTz,
                calendarEventId: calendarEventIdWhenPatching,
            });

            updateGoalEvent(event, undefined, newDateStr, newDateTz);
        },
        [patchPartialPayload, calendarEventIdWhenPatching, event]
    );

    const removeDate = React.useCallback(() => {
        patchPartialPayload({ date: '', tz: '', isCompleted: false });
    }, [patchPartialPayload, event]);

    const patchTitle = React.useCallback(
        (title: string | undefined = '') => {
            const trimmedTitle = title.trim();
            setCurrentTitle(trimmedTitle);
            patchPartialPayload({
                title: trimmedTitle,
                calendarEventId: calendarEventIdWhenPatching,
            });

            updateGoalEvent(event, trimmedTitle, undefined, undefined);
        },
        [patchPartialPayload, calendarEventIdWhenPatching, event]
    );

    const patchIsCompleted = React.useCallback(
        (isCompleted: boolean) => patchPartialPayload({ isCompleted }),
        [patchPartialPayload]
    );

    // Detect if the calendar should exist or not and act accordingly.
    const eventShouldExist = !!title && !!date && !!tz;
    const hasCalendarEventId = !!calendarEventId;

    // A goal with a title and date needs to shows up in the calendar. Create if we don't have an item ID.
    // NOTE: there is a chance we might succeed in creating the event and the fail to patch the payload,
    //       and that can cause us to create other events. A possible solution would be to include the
    //       ID of the asset in the event and then look for it before we create a new one.
    //       We can implement this detection mechanism later.
    React.useEffect(
        function createEventThatShouldExist() {
            if (eventShouldExist && !hasCalendarEventId) {
                createGoalEvent(
                    effectiveTitle,
                    effectiveDate,
                    effectiveTz,
                    projectName,
                    projectUrl
                ).then(e => e && patchPartialPayload({ calendarEventId: e.ItemId.Id }));
            }
        },
        [
            eventShouldExist,
            hasCalendarEventId,
            effectiveTitle,
            effectiveDate,
            effectiveTz,
            projectName,
            projectUrl,
        ]
    );

    // A goal without title or date should not exist in the calendar. Delete if we have one.
    React.useEffect(
        function deleteEventThatShouldNotExist() {
            if (event && !eventShouldExist) {
                deleteGoalEvent(event).then(() => {
                    patchPartialPayload({ calendarEventId: '' });
                });
            }
        },
        [event, eventShouldExist]
    );

    // Current title start with what we have in the payload but it can get updated
    // with changes that happen in Calendar, as long as the value was not edited.
    const [currentTitle, setCurrentTitle] = React.useState(effectiveTitle);
    React.useEffect(
        function syncEventChangesToCurrentTitleEditor() {
            if (currentTitle == title && title != effectiveTitle) {
                setCurrentTitle(effectiveTitle);
            }
        },
        [currentTitle, title, effectiveTitle, setCurrentTitle]
    );

    // If we detect changes in the calendar event, update the goal payload with them.
    React.useEffect(
        function syncEventChangesToGoalPayload() {
            if (
                eventShouldExist &&
                hasCalendarEventId &&
                (effectiveTitle != title || effectiveDate != date || effectiveTz != tz)
            ) {
                patchPartialPayload({
                    title: effectiveTitle,
                    date: effectiveDate,
                    tz: effectiveTz,
                });
            }
        },
        [
            eventShouldExist,
            hasCalendarEventId,
            effectiveTitle,
            effectiveDate,
            effectiveTz,
            title,
            date,
            tz,
        ]
    );

    return {
        title: effectiveTitle,
        dueDate: effectiveDueDate,
        isCompleted,
        patchDate,
        removeDate,
        patchTitle,
        patchIsCompleted,
        currentTitle,
        setCurrentTitle,
    };
}
